import { useEffect } from 'react'
import IMarketplace from 'service/IMarketplace'

type Func = (...args: any[]) => string

export const preffixTestIds =
  (preffix: string) =>
  <
    const M extends {
      [k: string]: string | Func
    }
  >(
    testIds: M
  ) => {
    for (let key in testIds)
      if (typeof testIds[key] === 'function') {
        const originalFunction = testIds[key] as Func
        testIds[key] = ((...args: any[]) =>
          `${preffix}-${originalFunction(...args)}`) as any
      } else testIds[key] = `${preffix}-${testIds[key]}` as any
    return testIds
  }

type CypressRequirement = 'chain' | 'tenant'
class RequirementsError extends Error {
  requirements: string[]
  constructor(unsetRequirements: string[]) {
    super()
    this.requirements = unsetRequirements
  }
}
class RequirementChecker {
  requiredRequirements = new Set<CypressRequirement>()

  needs(requirement: CypressRequirement) {
    this.requiredRequirements.add(requirement)

    switch (requirement) {
      case 'chain':
        this.needs('tenant')
    }

    return this
  }

  private setup(requirement: CypressRequirement): string {
    switch (requirement) {
      case 'chain':
        return 'Please set it on TenantBuilder'
      case 'tenant':
        return 'Please set it on CenarioBuilder'
    }
  }

  check(availableRequirements: Set<CypressRequirement>) {
    const unsetRequirements = Array.from(this.requiredRequirements).filter(
      (req) => {
        return !availableRequirements.has(req)
      }
    )
    const unsetRequirementsSetup = unsetRequirements.map(
      (req) => `To setup ${req}: ${this.setup(req)}`
    )
    if (unsetRequirementsSetup.length)
      throw new RequirementsError(unsetRequirementsSetup)
  }
}
class CypressRequirementsCheckerCls {
  private availableRequirements = new Set<CypressRequirement>()
  private delayTimers: {
    [id: string]: NodeJS.Timeout | undefined
  } = {}

  private timer(timerId: string) {
    return {
      cancel: (timerId: string) => {
        clearTimeout(this.delayTimers[timerId])
      },
      check: (checkFunc: () => void) => {
        this.delayTimers[timerId] = setTimeout(() => {
          checkFunc()
        }, 1000)
      },
    }
  }

  has(requirement: CypressRequirement, has: boolean) {
    if (has) this.availableRequirements.add(requirement)
    else this.availableRequirements.delete(requirement)
  }

  hasMarketplaceSetWhenSearchingOffers(
    marketplaceInstance: IMarketplace | undefined
  ) {
    const timer = this.timer('marketplace')
    if (!marketplaceInstance) {
      timer.check(() => {
        // Needs chain
        try {
          new RequirementChecker()
            .needs('chain')
            .check(this.availableRequirements)
        } catch (_e: any) {
          const e = _e as RequirementsError
          alert(
            `There is no marketplace instance set, please setup using GalleryBuilder

${`Missing pre requirements:
${e.requirements.join('\n')}`}`
          )
        }
      })
    } else {
      timer.cancel('marketplace')
    }
  }
}

/** Used for warning during cypress testing that some requirement for something is missing */
export const CypressRequirementsChecker =
  process.env.CYPRESS || window.Cypress
    ? new CypressRequirementsCheckerCls()
    : ({
        has() {},
        hasMarketplaceSetWhenSearchingOffers() {},
      } as Omit<CypressRequirementsCheckerCls, "">)

export function useCypressRequirementChecker(
  requirement: CypressRequirement,
  relatedVariable: any
) {
  useEffect(() => {
    CypressRequirementsChecker.has(requirement, !!relatedVariable)
  }, [relatedVariable])
}
